import leaflet from 'leaflet';
// Bundled as `?url` for the same reason PrintWindowStructure does it: Vite would otherwise
// inline marker-icon.png as a data URI and Leaflet's icon-path heuristic would come up empty.
import leafletIconRetinaUrl from 'leaflet/dist/images/marker-icon-2x.png?url';
import leafletIconUrl from 'leaflet/dist/images/marker-icon.png?url';
import leafletShadowUrl from 'leaflet/dist/images/marker-shadow.png?url';
import leafletCss from 'leaflet/dist/leaflet.css?inline';
import React from 'react';

import CSS_PRINT from './CssPrint';
import CSS_PUBLIC_REPORT from './CssPublicReport';
import CSS_RESET from './CssReset';
import {useFontsReady} from './readyState';
import {LeafletContext} from './ReportExportWindow';

/**
 * The report's page structure when it renders in the current document rather than a popup.
 *
 * Sibling of PrintWindowStructure, which exists to bridge a popup window: it loads Leaflet
 * through a <script> tag so the library and its DOM share a document. In-document there is
 * no such split, so the statically imported module goes straight into the context.
 *
 * Blueprint's CSS is already global here, so only the report's own styles are inlined —
 * with CSS_RESET after Blueprint, the layering the popup uses. Both are document-wide while
 * this is mounted, which is safe only because /p/r/:uuid renders no app chrome.
 */
export const ReportDocumentStructure: React.FunctionComponent<
  React.PropsWithChildren<{
    title: string;
    fontsToCheck?: string[];
    onFontsReady?: () => void;
    onFontsTimeout?: () => void;
  }>
> = ({title, fontsToCheck, onFontsReady, onFontsTimeout, children}) => {
  React.useEffect(() => {
    document.title = title;
  }, [title]);

  React.useLayoutEffect(() => {
    leaflet.Icon.Default.mergeOptions({
      iconUrl: leafletIconUrl,
      iconRetinaUrl: leafletIconRetinaUrl,
      shadowUrl: leafletShadowUrl,
    });
  }, []);

  useFontsReady(() => document, fontsToCheck, onFontsReady, onFontsTimeout);

  return (
    <>
      <style type="text/css">
        {leafletCss}
        {CSS_RESET}
        {CSS_PRINT}
        {CSS_PUBLIC_REPORT}
      </style>
      <LeafletContext.Provider value={leaflet}>{children}</LeafletContext.Provider>
    </>
  );
};
